import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAutomationClient } from '../providers/AutomationProvider'
import type {
  AlertHistoryListParams,
  CreateAlertRuleParams,
  UpdateAlertRuleParams,
  CreateAlertMuteParams,
} from '../types'

/**
 * Query key factory for alerts-related queries
 */
export const alertsKeys = {
  all: ['alerts'] as const,
  // Rules
  rules: () => [...alertsKeys.all, 'rules'] as const,
  rulesList: () => [...alertsKeys.rules(), 'list'] as const,
  ruleDetail: (id: string) => [...alertsKeys.rules(), 'detail', id] as const,
  // History
  history: () => [...alertsKeys.all, 'history'] as const,
  historyList: (params?: AlertHistoryListParams) => [...alertsKeys.history(), 'list', params] as const,
  historyDetail: (id: string) => [...alertsKeys.history(), 'detail', id] as const,
  // Mutes
  mutes: () => [...alertsKeys.all, 'mutes'] as const,
  mutesList: () => [...alertsKeys.mutes(), 'list'] as const,
  // Stats
  stats: () => [...alertsKeys.all, 'stats'] as const,
}

// ============ ALERT RULES HOOKS ============

/**
 * Hook to fetch all alert rules
 */
export function useAlertRules() {
  const { client } = useAutomationClient()

  return useQuery({
    queryKey: alertsKeys.rulesList(),
    queryFn: () => client.alerts.rules.list(),
  })
}

/**
 * Hook to fetch a single alert rule
 */
export function useAlertRule(id: string, options?: { enabled?: boolean }) {
  const { client } = useAutomationClient()

  return useQuery({
    queryKey: alertsKeys.ruleDetail(id),
    queryFn: () => client.alerts.rules.get(id),
    enabled: options?.enabled !== false && !!id,
  })
}

/**
 * Hook to create an alert rule
 */
export function useCreateAlertRule() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateAlertRuleParams) => client.alerts.rules.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: alertsKeys.rulesList() })
      queryClient.invalidateQueries({ queryKey: alertsKeys.stats() })
    },
  })
}

/**
 * Hook to update an alert rule
 */
export function useUpdateAlertRule() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAlertRuleParams }) =>
      client.alerts.rules.update(id, data),
    onSuccess: (data, { id }) => {
      queryClient.invalidateQueries({ queryKey: alertsKeys.rulesList() })
      queryClient.setQueryData(alertsKeys.ruleDetail(id), data)
      queryClient.invalidateQueries({ queryKey: alertsKeys.stats() })
    },
  })
}

/**
 * Hook to delete an alert rule
 */
export function useDeleteAlertRule() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => client.alerts.rules.delete(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: alertsKeys.rulesList() })
      queryClient.removeQueries({ queryKey: alertsKeys.ruleDetail(id) })
      queryClient.invalidateQueries({ queryKey: alertsKeys.stats() })
    },
  })
}

/**
 * Hook to test an alert rule (sends test alert)
 */
export function useTestAlertRule() {
  const { client } = useAutomationClient()

  return useMutation({
    mutationFn: (id: string) => client.alerts.rules.test(id),
  })
}

// ============ ALERT HISTORY HOOKS ============

/**
 * Hook to fetch alert history
 */
export function useAlertHistory(params?: AlertHistoryListParams) {
  const { client } = useAutomationClient()

  return useQuery({
    queryKey: alertsKeys.historyList(params),
    queryFn: () => client.alerts.history.list(params),
  })
}

/**
 * Hook to fetch a single alert
 */
export function useAlert(id: string, options?: { enabled?: boolean }) {
  const { client } = useAutomationClient()

  return useQuery({
    queryKey: alertsKeys.historyDetail(id),
    queryFn: () => client.alerts.history.get(id),
    enabled: options?.enabled !== false && !!id,
  })
}

/**
 * Hook to acknowledge an alert
 */
export function useAcknowledgeAlert() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, userId }: { id: string; userId?: string }) =>
      client.alerts.history.acknowledge(id, userId),
    onSuccess: (data, { id }) => {
      queryClient.invalidateQueries({ queryKey: alertsKeys.history() })
      queryClient.setQueryData(alertsKeys.historyDetail(id), data)
      queryClient.invalidateQueries({ queryKey: alertsKeys.stats() })
    },
  })
}

/**
 * Hook to resolve an alert
 */
export function useResolveAlert() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => client.alerts.history.resolve(id),
    onSuccess: (data, id) => {
      queryClient.invalidateQueries({ queryKey: alertsKeys.history() })
      queryClient.setQueryData(alertsKeys.historyDetail(id), data)
      queryClient.invalidateQueries({ queryKey: alertsKeys.stats() })
    },
  })
}

// ============ ALERT MUTES HOOKS ============

/**
 * Hook to fetch all alert mutes
 */
export function useAlertMutes() {
  const { client } = useAutomationClient()

  return useQuery({
    queryKey: alertsKeys.mutesList(),
    queryFn: () => client.alerts.mutes.list(),
  })
}

/**
 * Hook to create an alert mute
 */
export function useCreateAlertMute() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateAlertMuteParams) => client.alerts.mutes.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: alertsKeys.mutesList() })
      queryClient.invalidateQueries({ queryKey: alertsKeys.stats() })
    },
  })
}

/**
 * Hook to delete an alert mute
 */
export function useDeleteAlertMute() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => client.alerts.mutes.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: alertsKeys.mutesList() })
      queryClient.invalidateQueries({ queryKey: alertsKeys.stats() })
    },
  })
}

// ============ ALERT STATS HOOK ============

/**
 * Hook to fetch alert statistics
 */
export function useAlertStats() {
  const { client } = useAutomationClient()

  return useQuery({
    queryKey: alertsKeys.stats(),
    queryFn: () => client.alerts.stats(),
  })
}
